import { useDialogFocus } from "./Dialog";
import React, { useState, useRef } from "react";
import { X, Wallet, MessageSquare } from "lucide-react";
import { authHeaders } from "../firebase/authHeaders";

interface BuyChatCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatCredits: number;
  uid: string;
}

const CHAT_PRICE_ID = "price_1TG1miA409CfsIzFKXfxBxgT";
const CHECKOUT_URL =
  "https://createcheckoutsession-mt62q3wpaa-uc.a.run.app/createCheckoutSession";

const BuyChatCreditsModal: React.FC<BuyChatCreditsModalProps> = ({
  isOpen,
  onClose,
  chatCredits,
  uid,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, isOpen, onClose);

  if (!isOpen) return null;

  const handlePurchase = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(CHECKOUT_URL, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ priceId: CHAT_PRICE_ID, uid }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to create checkout session");
      }
      const { url } = await response.json();
      window.location.href = url;
    } catch (err: any) {
      console.error("Checkout error:", err);
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const isLow = (chatCredits ?? 0) < 0.5;

  return (
    <div className="motion-fade fixed inset-0 z-[100] flex items-center justify-center bg-overlay/50 backdrop-blur-sm p-4">
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Chat and video credits" className="motion-dialog bg-surface rounded-lg shadow-2xl w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-success-soft rounded-lg">
              <Wallet size={18} className="text-success" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-content">Chat Credits</h2>
              <p className="text-xs text-muted">
                Top up to use paid AI models
              </p>
            </div>
          </div>
          <button
            onClick={onClose} aria-label="Close credits"
            className="p-2 hover:bg-raised rounded-md transition-colors"
          >
            <X size={18} className="text-muted" />
          </button>
        </div>

        {/* Balance */}
        <div className="mx-5 mb-4 rounded-xl border-2 border-success-line bg-success-soft p-4">
          <div className="text-xs font-semibold text-success uppercase tracking-wide mb-1">
            Current balance
          </div>
          <div
            className={`text-2xl font-semibold ${isLow ? "text-warning" : "text-success"}`}
          >
            ${(chatCredits ?? 0).toFixed(2)}
          </div>
          {isLow && (
            <div className="text-xs text-warning font-medium mt-1">
              {(chatCredits ?? 0) <= 0
                ? "No credits — top up to continue"
                : "Running low"}
            </div>
          )}
        </div>

        {/* What it unlocks */}
        <div className="mx-5 mb-4 flex items-start gap-2 text-xs text-muted">
          <MessageSquare
            size={13}
            className="mt-0.5 flex-shrink-0 text-muted"
          />
          <span>
            Chat with any paid model (Claude, GPT-5, Gemini, Grok…). Balance
            drains per message based on actual usage. Free models are always
            free.
          </span>
        </div>

        {error && (
          <div className="mx-5 mb-3 bg-danger-soft border border-danger-line text-danger text-sm px-4 py-2.5 rounded-lg">
            {error}
          </div>
        )}

        {/* Buy button */}
        <div className="px-5 pb-5">
          <button
            onClick={handlePurchase}
            disabled={loading}
            className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-sm transition-all ${
              loading
                ? "bg-success-solid text-on-solid cursor-not-allowed"
                : "bg-success-solid hover:bg-success-hover text-on-solid shadow-md hover:shadow-lg"
            }`}
          >
            {loading ? (
              <>
                <span className="animate-pulse">Redirecting to checkout…</span>
              </>
            ) : (
              <>
                <Wallet size={16} />
                Add $5.00 to balance
              </>
            )}
          </button>
          <p className="text-center text-xs text-muted mt-2.5">
            Secure checkout · Stripe · No subscription
          </p>
        </div>
      </div>
    </div>
  );
};

export default BuyChatCreditsModal;
